/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="purchaseOrder.module.ts" />
/// <reference path="../../components/common/lotModalBridge.helper.ts" />

module Meevo.Plugins.PurchaseOrder {

    /**
     * Input shape for the <meevo-lot-tracking-modal> custom element's `lineItem`
     * property when invoked from the PO Receive flow. Mirrors the web component's
     * `PoReceiveLineItem` arm of the `LotTrackingLineItem` discriminated union
     * (see Web.Hub web-components/.../models/po-receive-line-item.ts).
     *
     * `context: 'po-receive'` is the union discriminator — required so the
     * modal's internal type-narrowing knows which arm it has.
     */
    export interface ILotModalInput {
        context: 'po-receive';
        variantId: string;
        productInventoryTypeEnum: number;
        orderOfEntry: number;
        poReceivableHeaderId: string;
        quantityExpected: number;
        quantityAlreadyReceived: number;
        receiveQuantity: number;
        /** Product-level display name — first segment after SKU in the modal subtitle. */
        productDisplayName: string;
        /** Variant modifiers (e.g. size/color). Rendered after productDisplayName with a " - " separator only when non-empty. */
        variantDisplayName: string;
        sku: string;
        /** Product barcode — trailing segment of the modal subtitle. */
        barCode: string;
        inventoryTypeDisplay: string;
        trackExpirationDate: boolean;
    }

    /**
     * Allocation shape used for both `existingAllocations` input and the `saved` event payload.
     * Mirrors the web component's VariantLotAllocation interface.
     */
    export interface ILotAllocation {
        variantLot_TId: string;
        lotNumber: string;
        expirationDate: string | null;
        quantity: number;
        /**
         * Client-only flag (underscore prefix marks "not a backend field").
         * openLotModal stamps `true` on allocations emitted by modal-Apply;
         * server-sourced committed allocations arrive without it. Drives
         * both lotButtonMode (partial vs editable) and the modal's split
         * between locked and editable rows. The next server refresh
         * (savePurchaseOrderAndReceival replaces poReceival wholesale)
         * returns data without the flag, so pending entries collapse back
         * into "received" state without explicit cleanup. The backend
         * never sees this field — it rides along in the save payload and
         * is ignored by the C# deserializer (unknown property).
         */
        _pending?: boolean;
    }

    /**
     * Re-export of the shared mode type so existing PO call sites that import
     * `LotModalMode` from this module continue to compile unchanged.
     */
    export type LotModalMode = Meevo.Components.Common.LotModalMode;

    /**
     * Thin AngularJS bridge for the <meevo-lot-tracking-modal> Angular 20
     * Elements web component, scoped to the PO Receive flow.
     *
     * Now a wrapper around the shared `LotModalBridgeHelper`: every plugin that
     * opens the lot modal hits the same host-bridge construction, session
     * teardown wiring, custom-element-registration check, and error path. The
     * only PO-specific bit is the `context: 'po-receive'` literal and the
     * line-item / allocation type signatures — all other behavior lives in the
     * helper.
     */
    export class LotModalBridge {

        static $inject = [
            'meevo.components.common.LotModalBridgeHelper',
        ];

        constructor(
            private helper: Meevo.Components.Common.LotModalBridgeHelper,
        ) { }

        /**
         * Public API: mount the lot-tracking-modal for the given PO receival line item.
         *
         * @param lineItem              The PO receival row being edited (variant, expected/received qty, etc.)
         * @param existingAllocations   Pre-existing allocations for partial/readonly modes (empty for editable)
         * @param mode                  'editable' | 'partial' | 'readonly'
         * @param onSaved               Invoked with the final allocations array when the user saves
         * @param onCancelled           Invoked when the user cancels (X, Escape, or Cancel button)
         */
        public open(
            lineItem: ILotModalInput,
            existingAllocations: ILotAllocation[],
            mode: LotModalMode,
            onSaved?: (allocations: ILotAllocation[]) => void,
            onCancelled?: () => void,
        ): void {
            this.helper.openModal<ILotModalInput, ILotAllocation>({
                context: 'po-receive',
                mode: mode,
                lineItem: lineItem,
                domProps: { existingAllocations: existingAllocations },
                onSaved: onSaved,
                onCancelled: onCancelled,
            });
        }
    }

    angular.module('meevo.plugins.purchaseOrder')
        .service('meevo.plugins.purchaseOrder.LotModalBridge', LotModalBridge);
}
